/**
 * Shortlist → angles → Web Intent → Done. (Brief §3.3)
 *
 * La liste est une pile d'onglets, façon sélecteur de tabs : chaque tweet gardé
 * est une page dont on ne voit que le chrome et le haut, la suivante recouvrant
 * le reste. Taper un onglet le ramène au premier plan, sa croix le referme. La
 * mécanique spatiale et le chrome vivent dans `ShortlistStack.tsx` ; ce fichier
 * ne s'occupe que du contenu et du parcours de reply :
 *
 *   1. « Angles » appelle Haiku (ou sert le cache) et propose trois entrées
 *   2. le brouillon est TOUJOURS éditable — c'est une contrainte du brief
 *   3. « Reply » ouvre le composer X pré-rempli, « Done » journalise
 *
 * L'app ne peut pas savoir si la reply a été postée : « Done » est déclaratif.
 */

import { memo, useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { buildReplyIntentUrl, buildTweetUrl, type DeckCard } from "@rm/shared";

import { api, ApiError } from "../lib/api.js";
import { Avatar, CARD_SURFACE, ScoreGem, formatAge } from "../components/SwipeCard.js";
import { Stack, StackItem, TabChrome } from "../components/ShortlistStack.js";
import { SURFACE } from "../components/shortlist-geometry.js";
import { Badge, Button, Empty, Spinner, Textarea } from "../components/ui.js";

interface Angle {
  id: number;
  idx: number;
  intent: string;
  draft: string;
  asset_hint: string | null;
}

/**
 * Le DÉCOR de la scène : `SURFACE.pageBg` posé sur le conteneur de la
 * shortlist, et sur lui seul (§D46).
 *
 * Scopé exprès : la coquille de l'app et sa barre de navigation gardent
 * `--color-ink`. Ce qu'on règle ici, c'est ce qu'il y a DERRIÈRE la pile, pas
 * le thème de l'app.
 *
 * Le style est écrit à la main plutôt que rendu : le banc mute l'objet et
 * re-pose la scène par un `resize`, sans état React à remonter — c'est la
 * convention de tous les réglages (§D41). L'effet tourne à chaque rendu, ce
 * qui couvre aussi le passage du chargement à la liste, où le nœud n'existait
 * pas encore.
 */
export function usePageBackground(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const paint = () => {
      if (ref.current) ref.current.style.background = SURFACE.pageBg;
    };
    paint();
    window.addEventListener("resize", paint);
    return () => window.removeEventListener("resize", paint);
  });
}

export function useExpandedHeight(): number {
  const [h, setH] = useState(() => Math.min(window.innerHeight * 0.74, 620));
  useEffect(() => {
    const onResize = () => setH(Math.min(window.innerHeight * 0.74, 620));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return h;
}

/**
 * Le titre d'un onglet : l'avatar en guise de favicon, le nom, la fraîcheur.
 *
 * La fraîcheur est dans le titre et non dans la page parce qu'elle décide de
 * l'ordre dans lequel on traite la pile — c'est une information d'onglet, au
 * même titre que le nom.
 */
function TabTitle({ card }: { card: DeckCard }) {
  const handle = card.author?.handle ?? card.author_handle ?? "?";
  return (
    <>
      <Avatar url={card.author?.avatar_url} size={17} />
      <span className="truncate">{card.author?.display_name ?? `@${handle}`}</span>
      <span className="shrink-0 font-normal text-[var(--color-muted)]">
        · {formatAge(card.age_minutes)}
      </span>
    </>
  );
}

/**
 * Le chargement de la liste, partagé avec le rendu GL (§D43).
 *
 * Rafraîchissement en place, sans remontage : la liste se recharge, les
 * cartes déjà en scène gardent leur pose (clés stables sur tweet_id), les
 * nouvelles entrent en bout de trajet, sous le bord bas de l'écran. Le banc
 * s'en sert pour simuler l'arrivée continue de cartes ; plus tard, une
 * notification push pourra déclencher le même événement.
 *
 * `null` tant que rien n'est chargé — les deux moteurs s'en servent pour ne
 * rien peindre avant d'avoir la liste.
 */
export function useShortlistCards(): DeckCard[] | null {
  const [cards, setCards] = useState<DeckCard[] | null>(null);

  const load = useCallback(async () => {
    const { cards } = await api.get<{ cards: DeckCard[] }>("/deck/shortlist");
    setCards(cards);
  }, []);

  useEffect(() => {
    load().catch(() => setCards([]));
  }, [load]);

  useEffect(() => {
    const refresh = () => load().catch(() => undefined);
    window.addEventListener("rm:shortlist-refresh", refresh);
    return () => window.removeEventListener("rm:shortlist-refresh", refresh);
  }, [load]);

  return cards;
}

export function ShortlistPage() {
  const [cards, setCards] = useState<DeckCard[] | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const expandedHeight = useExpandedHeight();
  const rootRef = useRef<HTMLDivElement>(null);

  /**
   * Liste verrouillée tant qu'une carte est ouverte. Sans ça, la molette
   * continue de faire tourner la roue SOUS la carte — invisible sur le
   * moment, et la fermeture retombe ailleurs : le « saut ». On fige le
   * scroller ancêtre (main aujourd'hui, sans le nommer) ; le contenu de la
   * carte garde son propre défilement interne. Les barres étant déjà
   * masquées globalement, le verrou ne fait pas bouger la mise en page.
   */
  useEffect(() => {
    if (openIndex === null) return;
    let el: HTMLElement | null = rootRef.current;
    while (
      el &&
      !(el.scrollHeight > el.clientHeight && /auto|scroll/.test(getComputedStyle(el).overflowY))
    ) {
      el = el.parentElement;
    }
    if (!el) return;
    const scroller = el;
    const previous = scroller.style.overflowY;
    scroller.style.overflowY = "hidden";
    return () => {
      scroller.style.overflowY = previous;
    };
  }, [openIndex]);

  const load = useCallback(async () => {
    const { cards } = await api.get<{ cards: DeckCard[] }>("/deck/shortlist");
    setCards(cards);
  }, []);

  useEffect(() => {
    load().catch(() => setCards([]));
  }, [load]);

  /**
   * Rafraîchissement en place, sans remontage : la liste se recharge, les
   * cartes déjà en scène gardent leur pose (clés stables sur tweet_id), les
   * nouvelles entrent en bout de roue, sous le bord bas de l'écran. Le banc
   * s'en sert pour simuler l'arrivée continue de cartes ; plus tard, une
   * notification push pourra déclencher le même événement.
   */
  useEffect(() => {
    const refresh = () => load().catch(() => undefined);
    window.addEventListener("rm:shortlist-refresh", refresh);
    return () => window.removeEventListener("rm:shortlist-refresh", refresh);
  }, [load]);

  // Échap referme la pile : le geste de retour doit exister au clavier aussi.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenIndex(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (cards === null) return <Spinner />;

  if (cards.length === 0) {
    return (
      <Empty
        title="Shortlist vide"
        hint="Swipe des cartes à droite depuis le deck pour les retrouver ici."
      />
    );
  }

  return (
    /* Pleine largeur : la scène s'étend d'un bord à l'autre — les marges
       vivaient ici en px-4, et la carte de lecture, budgétée pour atteindre
       exactement la largeur mesurée, mourait 16 px avant les bords. */
    <div ref={rootRef} className="pt-4">
      {/* Pas d'en-tête : la scène occupe l'écran entier, les cartes fondent en
          sortant par le haut. Le nombre de tweets en attente est une info de
          navigation, pas d'écran — elle trouvera sa place en pastille sur
          l'onglet Shortlist à la passe DA. */}
      {/* `Stack` passe la position du centre du viewport : c'est elle qui donne
          à chaque carte son angle propre. */}
      <Stack count={cards.length} openIndex={openIndex} expandedHeight={expandedHeight}>
        {(focus, width, speed, stageTop) => cards.map((card, index) => (
          <StackItem
            key={card.tweet_id}
            index={index}
            count={cards.length}
            focus={focus}
            width={width}
            speed={speed}
            stageTop={stageTop}
            openIndex={openIndex}
            expandedHeight={expandedHeight}
            onSelect={() => setOpenIndex(index)}
            onClose={() => setOpenIndex(null)}
          >
            <TweetCard
              card={card}
              open={openIndex === index}
              onClose={() => setOpenIndex(null)}
              onDone={() => {
                setCards((c) => (c ?? []).filter((x) => x.tweet_id !== card.tweet_id));
                setOpenIndex(null);
              }}
            />
          </StackItem>
        ))}
      </Stack>
    </div>
  );
}

/**
 * LA carte — la même, ouverte ou fermée. (Principe de Vincent)
 *
 * Ouvert et fermé ne sont pas deux vues : c'est un seul objet, avec le même
 * chrome, le même corps — mêmes nœuds DOM, donc rien ne permute pendant que
 * la carte voyage. L'état ouvert AJOUTE des éléments sous le corps commun
 * (lien vers X, topics, parcours de réponse) et rend le contenu défilable ;
 * il ne remplace rien. Bonus de la fusion : l'état du parcours (angles,
 * brouillon) vit dans la carte et survit à une fermeture/réouverture tant
 * qu'elle reste montée.
 *
 * **L'aperçu est du décor, le titre est l'information** (fermé) : corps fixe
 * 14 px, en retrait — la colonne des titres reste ce qu'on scanne. Pas de
 * `line-clamp` : le texte est coupé par le bord de la carte, « ça continue
 * dessous ». Ouvert, le même corps passe en pleine encre, sans changer de
 * taille : même carte.
 *
 * La vue liste n'a AUCUN bouton : toute la carte est la cible d'ouverture.
 * Retirer un tweet de la shortlist n'est pas un geste de survol — le
 * parcours de la carte ouverte s'en chargera.
 */
export const TweetCard = memo(function TweetCard({
  card,
  open,
  onClose,
  onDone,
}: {
  card: DeckCard;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [angles, setAngles] = useState<Angle[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);

  const handle = card.author?.handle ?? card.author_handle ?? "?";

  const loadAngles = useCallback(
    async (refresh = false) => {
      setBusy(true);
      setError(null);
      try {
        const response = await api.get<{ angles: Angle[] }>(
          `/angles/${card.tweet_id}${refresh ? "?refresh=1" : ""}`,
        );
        setAngles(response.angles);
        if (response.angles[0]) {
          setSelected(response.angles[0].id);
          setDraft(response.angles[0].draft);
        }
      } catch (err) {
        setError(
          err instanceof ApiError && err.code === "anthropic_missing"
            ? "Clé Anthropic absente côté serveur."
            : err instanceof Error
              ? err.message
              : "Génération impossible",
        );
      } finally {
        setBusy(false);
      }
    },
    [card.tweet_id],
  );

  async function logReply() {
    setBusy(true);
    try {
      await api.post("/replies", {
        tweet_id: card.tweet_id,
        angle_id: selected,
        draft_final: draft,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Log impossible");
      setBusy(false);
    }
  }

  return (
    <div
      className={`relative flex h-full flex-col overflow-hidden rounded-2xl ${
        open ? "" : "cursor-pointer"
      }`}
      style={CARD_SURFACE}
    >
      <TabChrome onAction={open ? onClose : undefined} right={<ScoreGem score={card.score} size={24} />}>
        <TabTitle card={card} />
      </TabChrome>

      {/* Zone défilante quand ouvert (et hors tirer-pour-fermer) ; simple
          fenêtre coupée par le bord de carte quand fermé. Le CORPS COMMUN est
          identique dans les deux cas. */}
      <div
        data-noclosedrag={open || undefined}
        className={
          open
            ? "min-h-0 flex-1 overflow-y-auto px-3.5 pb-4 pt-2"
            : "min-h-0 flex-1 overflow-hidden px-3.5 pt-2"
        }
      >
        <div className="flex gap-3">
          <p
            className="min-w-0 flex-1 transition-colors"
            style={{
              fontSize: "14px",
              lineHeight: "1.45",
              color: open ? "var(--color-text)" : "rgba(245,245,245,0.68)",
              /**
               * Fermé, l'aperçu s'arrête sur des lignes ENTIÈRES. La coupe
               * par occlusion — la carte suivante qui tranche une ligne en
               * plein glyphe — se lisait comme du flou (retour de Vincent,
               * qui révise le « pas de line-clamp » de D36) : borné à trois
               * lignes, le bord de la carte suivante tombe sur de la surface
               * vide, jamais sur du texte.
               */
              ...(open
                ? {}
                : {
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical" as const,
                    WebkitLineClamp: 3,
                    overflow: "hidden",
                  }),
            }}
          >
            {card.text}
          </p>
          {card.media_url && (
            <img
              src={card.media_url}
              alt=""
              className="h-12 w-12 shrink-0 rounded-md object-cover ring-1 ring-white/10"
              style={{ opacity: open ? 1 : 0.8 }}
              loading="lazy"
            />
          )}
        </div>

        {/* Tout ce qui suit est EN PLUS — l'état ouvert n'enlève rien. */}
        {open && (
          <>
            <div className="mt-2 flex items-center gap-2">
              <a
                href={buildTweetUrl(handle, card.tweet_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[var(--color-muted)] transition hover:text-[var(--color-text)]"
              >
                @{handle} ↗
              </a>
              {card.topics.slice(0, 3).map((topic) => (
                <Badge key={topic}>{topic}</Badge>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-3 border-t border-white/[0.07] pt-4">
              {angles === null ? (
                <Button onClick={() => loadAngles()} disabled={busy} variant="primary" full>
                  {busy ? "Génération…" : "Proposer 3 angles"}
                </Button>
              ) : (
                <>
                  <div className="flex flex-col gap-2">
                    {angles.map((angle) => (
                      <button
                        key={angle.id}
                        onClick={() => {
                          setSelected(angle.id);
                          setDraft(angle.draft);
                        }}
                        className={`rounded-lg border p-3 text-left transition ${
                          selected === angle.id
                            ? "border-[var(--color-accent)] bg-[#141c2c]"
                            : "border-[var(--color-line)] bg-white/[0.03]"
                        }`}
                      >
                        <p className="text-xs font-medium text-[var(--color-accent)]">
                          {angle.intent}
                        </p>
                        <p className="mt-1 text-sm">{angle.draft}</p>
                        {angle.asset_hint && (
                          <p className="mt-1.5 text-xs text-[var(--color-warn)]">
                            → joins {angle.asset_hint}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>

                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Ton brouillon, édité."
                  />
                  <p className="-mt-1 text-right text-[11px] tabular-nums text-[var(--color-muted)]">
                    {draft.length} caractères
                  </p>

                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      full
                      disabled={!draft.trim()}
                      onClick={() => {
                        window.open(
                          buildReplyIntentUrl(card.tweet_id, draft),
                          "_blank",
                          "noopener,noreferrer",
                        );
                        setOpened(true);
                      }}
                    >
                      Reply
                    </Button>
                    <Button full disabled={busy || !opened} onClick={logReply}>
                      Done
                    </Button>
                  </div>

                  {!opened && (
                    <p className="text-center text-[11px] text-[var(--color-muted)]">
                      « Done » s'active après avoir ouvert le composer X.
                    </p>
                  )}

                  <button
                    onClick={() => loadAngles(true)}
                    disabled={busy}
                    className="self-start text-xs text-[var(--color-muted)] underline underline-offset-2"
                  >
                    Regénérer
                  </button>
                </>
              )}

              {error && <p className="text-xs text-[var(--color-bad)]">{error}</p>}
            </div>
          </>
        )}
      </div>

      {/* La coupe franche au bas de la carte de devant ressemblerait à un
          bug ; le voile la rend intentionnelle. Recouvert sur les autres. */}
      {!open && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-10"
          style={{
            background:
              "linear-gradient(to bottom, rgba(16,16,17,0) 0%, rgba(16,16,17,0.5) 55%, #101011 100%)",
          }}
        />
      )}
    </div>
  );
},
/**
 * Le défilement re-rend chaque StackItem à chaque frame — les poses en
 * dépendent — mais le CONTENU d'une carte ne change qu'avec son tweet ou son
 * état ouvert/fermé. Les handlers, recréés à chaque rendu de la page, sont
 * volontairement ignorés : leur comportement ne dépend que de la carte.
 */
(prev, next) => prev.card === next.card && prev.open === next.open);
